import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import { env } from "../lib/env";
import { clearPreviousQueryScope, sameQueryScope, type QueryScope } from "../lib/query-scope";
import { supabase } from "../lib/supabase";

export interface OrganizationMembership {
  id: string;
  organizationId: string;
  organizationName: string;
}

export type OrganizationAccessState =
  "loading" | "signed-out" | "selection-required" | "ready" | "no-membership" | "error";

interface OrganizationContextValue {
  memberships: OrganizationMembership[];
  activeMembership: OrganizationMembership | null;
  activeOrganizationId: string | null;
  state: OrganizationAccessState;
  error?: string;
  selectOrganization: (organizationId: string) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const demoMembership: OrganizationMembership = {
  id: "00000000-0000-4000-8000-000000000998",
  organizationId: "00000000-0000-4000-8000-000000000997",
  organizationName: "Cruzeiro Life (demonstração)",
};

export interface MembershipRow {
  id: string;
  organization_id: string;
  status: string;
  organizations:
    | { trade_name: string; legal_name: string; status: string }
    | { trade_name: string; legal_name: string; status: string }[]
    | null;
}

export function deriveOrganizationAccess(
  memberships: OrganizationMembership[],
): OrganizationAccessState {
  if (memberships.length === 0) return "no-membership";
  if (memberships.length === 1) return "ready";
  return "selection-required";
}

export function mapOrganizationMembership(row: MembershipRow): OrganizationMembership | null {
  const organization = Array.isArray(row.organizations) ? row.organizations[0] : row.organizations;
  if (row.status !== "ACTIVE" || organization?.status !== "ACTIVE") return null;
  return {
    id: row.id,
    organizationId: row.organization_id,
    organizationName: organization.trade_name || organization.legal_name,
  };
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [memberships, setMemberships] = useState<OrganizationMembership[]>(
    env.dataMode === "demo" ? [demoMembership] : [],
  );
  const [activeMembership, setActiveMembership] = useState<OrganizationMembership | null>(
    env.dataMode === "demo" ? demoMembership : null,
  );
  const [state, setState] = useState<OrganizationAccessState>(
    env.dataMode === "demo" ? "ready" : "loading",
  );
  const [error, setError] = useState<string>();
  const scopeRef = useRef<QueryScope>({ userId: null, organizationId: null });
  const userScopeIsCurrent =
    env.dataMode === "demo" || scopeRef.current.userId === (user?.id ?? null);

  const switchScope = useCallback(
    async (next: QueryScope) => {
      if (!sameQueryScope(scopeRef.current, next)) {
        await clearPreviousQueryScope(queryClient);
        scopeRef.current = next;
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (env.dataMode === "demo") return;
    if (authLoading) {
      setState("loading");
      return;
    }
    if (!user) {
      void switchScope({ userId: null, organizationId: null });
      setMemberships([]);
      setActiveMembership(null);
      setError(undefined);
      setState("signed-out");
      return;
    }

    let disposed = false;
    setState("loading");
    setError(undefined);
    void (async () => {
      await switchScope({ userId: user.id, organizationId: null });
      const { data, error: membershipError } = await supabase
        .from("organization_memberships")
        .select("id, organization_id, status, organizations(trade_name, legal_name, status)")
        .eq("profile_id", user.id)
        .eq("status", "ACTIVE");

      if (disposed) return;
      if (membershipError) {
        setMemberships([]);
        setActiveMembership(null);
        setState("error");
        setError("Não foi possível validar suas organizações. Tente entrar novamente.");
        return;
      }

      const active = ((data ?? []) as MembershipRow[])
        .map(mapOrganizationMembership)
        .filter((membership): membership is OrganizationMembership => membership !== null);
      setMemberships(active);
      if (active.length === 1) {
        await switchScope({ userId: user.id, organizationId: active[0].organizationId });
        if (disposed) return;
        setActiveMembership(active[0]);
        setState("ready");
      } else {
        setActiveMembership(null);
        setState(deriveOrganizationAccess(active));
      }
    })();

    return () => {
      disposed = true;
    };
  }, [authLoading, switchScope, user?.id]);

  const value = useMemo<OrganizationContextValue>(
    () => ({
      memberships,
      activeMembership: userScopeIsCurrent ? activeMembership : null,
      activeOrganizationId: userScopeIsCurrent ? (activeMembership?.organizationId ?? null) : null,
      state: userScopeIsCurrent ? state : "loading",
      ...(error ? { error } : {}),
      selectOrganization: async (organizationId) => {
        const selected = memberships.find(
          (membership) => membership.organizationId === organizationId,
        );
        if (!selected || !user) throw new Error("Organização indisponível para esta sessão.");
        await switchScope({ userId: user.id, organizationId: selected.organizationId });
        setActiveMembership(selected);
        setState("ready");
      },
    }),
    [activeMembership, error, memberships, state, switchScope, user?.id, userScopeIsCurrent],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error("useOrganization must be used inside OrganizationProvider.");
  return context;
}
